import { useEffect, useRef, useState } from "react";
import { api } from "../utils/helpers";

export const dokuSelectionKey = userId => userId ? `iio-doku-tenant-selection:${encodeURIComponent(userId)}` : null;

function readPreference(key) {
  try {
    const value = key && JSON.parse(localStorage.getItem(key));
    if (value?.version === 1 && typeof value.spaceId === "string") {
      const instances = value.instancesBySpace;
      return { version: 1, spaceId: value.spaceId, instancesBySpace: instances && typeof instances === "object" && !Array.isArray(instances) ? instances : {} };
    }
  } catch { /* Ein gesperrter Browserspeicher verhindert die Auswahl nicht. */ }
  return { version: 1, spaceId: "", instancesBySpace: {} };
}

// DokuPage wird je Benutzer neu gemountet. Gespeichert werden nur IDs, keine Zugriffsdaten.
export default function useDokuTenantContext({ spaces, userId, enabled }) {
  const key = dokuSelectionKey(userId);
  const preference = useRef(null);
  if (!preference.current) preference.current = readPreference(key);
  const restored = useRef(false);
  const [spaceId, setSpaceId] = useState("");
  const [instanceId, setInstanceId] = useState("");
  const [result, setResult] = useState({ spaceId: "", instances: [], loading: false, error: null });
  const resultRef = useRef(result);
  resultRef.current = result;
  const validSpaceId = spaces.some(space => space.id === spaceId) ? spaceId : "";
  const instances = result.spaceId === validSpaceId ? result.instances : [];
  const validInstanceId = instances.some(instance => instance.id === instanceId) ? instanceId : "";

  function remember(next) {
    preference.current = next;
    try { if (key) localStorage.setItem(key, JSON.stringify(next)); } catch { /* Auswahl bleibt für diese Sitzung nutzbar. */ }
  }
  function selectSpace(id) {
    if (id && !spaces.some(space => space.id === id)) return;
    restored.current = true;
    remember({ ...preference.current, spaceId: id });
    if (id !== spaceId) {
      const remembered = preference.current.instancesBySpace[id];
      const cached = result.spaceId === id && !result.loading && !result.error;
      setInstanceId(cached && result.instances.some(instance => instance.id === remembered) ? remembered : "");
    }
    setSpaceId(id);
  }
  function selectInstance(id) {
    if (!validSpaceId || (id && !instances.some(instance => instance.id === id))) return;
    setInstanceId(id);
    remember({ ...preference.current, spaceId: validSpaceId, instancesBySpace: { ...preference.current.instancesBySpace, [validSpaceId]: id } });
  }

  useEffect(() => {
    if (!enabled || restored.current || !spaces.length) return;
    restored.current = true;
    if (spaces.some(space => space.id === preference.current.spaceId)) setSpaceId(preference.current.spaceId);
  }, [enabled, spaces]);

  useEffect(() => {
    if (!enabled || !validSpaceId) return;
    const previous = resultRef.current;
    // Ein Quellenwechsel darf bestätigte Package- und iFlow-Eingaben nicht zurücksetzen.
    if (previous.spaceId === validSpaceId && !previous.loading && !previous.error) return;
    let cancelled = false;
    setInstanceId("");
    setResult({ spaceId: validSpaceId, instances: [], loading: true, error: null });
    api(`/api/spaces/${encodeURIComponent(validSpaceId)}/instances`)
      .then(data => {
        if (cancelled) return;
        if (!Array.isArray(data)) throw Object.assign(new Error("Invalid instance response"), { code: "INVALID_INSTANCE_RESPONSE" });
        setResult({ spaceId: validSpaceId, instances: data, loading: false, error: null });
        const remembered = preference.current.instancesBySpace[validSpaceId];
        setInstanceId(data.some(instance => instance.id === remembered) ? remembered : "");
      })
      .catch(error => { if (!cancelled) setResult({ spaceId: validSpaceId, instances: [], loading: false, error }); });
    return () => { cancelled = true; };
  }, [enabled, validSpaceId]);

  return { spaceId: validSpaceId, instanceId: validInstanceId, instances, selectSpace, selectInstance,
    loading: Boolean(enabled && validSpaceId && (result.spaceId !== validSpaceId || result.loading)),
    error: result.spaceId === validSpaceId ? result.error : null };
}
